/**
 * PlanPage — the Plan tab: everything you set up once (income, bills, debts, savings, goals)
 * and where it's heading, on one scrolling page. The chips at the top jump to a section.
 */
import React, { useEffect, useRef, useState } from 'react';
import Paycheck from './Paycheck';
import Settings from './Settings';
import Debt from './Debt';
import Savings from './Savings';
import SinkingFunds from './SinkingFunds';
import NetWorthTracker from './NetWorthTracker';
import { BankBalanceCard } from './BankBalanceCard';
import CheckIn from './CheckIn';

export const PLAN_SECTIONS = [
  { id: 'budget',   label: '💰 Income & Budget' },
  { id: 'bills',    label: '📋 Bills' },
  { id: 'debt',     label: '📈 Debts' },
  { id: 'savings',  label: '💵 Savings & Goals' },
  { id: 'networth', label: '💎 Outlook' },
  { id: 'bank',     label: '🏦 Bank' },
  { id: 'checkin',  label: '📌 Check-In' },
];

// Old sub-tab ids that now live inside another section
const SECTION_ALIASES = { sinking: 'savings' };

export default function PlanPage({ sub, setSub, ...props }) {
  const { state, updateState, showToast } = props;
  const refs = useRef({});
  // The app header is sticky too, so the jump chips stick just below it
  const [hdrH, setHdrH] = useState(0);
  useEffect(() => {
    const measure = () => setHdrH(document.getElementById('hdr')?.offsetHeight || 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const jump = (id) => {
    const el = refs.current[SECTION_ALIASES[id] || id];
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - hdrH - 60, behavior: 'smooth' });
  };

  // Links from other tabs (e.g. "Log income →") arrive as a sub id: scroll there once
  useEffect(() => {
    if (!sub) return;
    jump(sub);
    setSub(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub]);

  const section = (id, children) => (
    <section key={id} ref={(el) => { refs.current[id] = el; }} className="plan-section">
      <h2 className="plan-h">{PLAN_SECTIONS.find((s) => s.id === id).label}</h2>
      {children}
    </section>
  );

  return (
    <div>
      <div className="subtabs plan-jump" style={{ top: hdrH }}>
        {PLAN_SECTIONS.map((s) => (
          <button key={s.id} className="subtab" onClick={() => jump(s.id)}>
            {s.label}
          </button>
        ))}
      </div>
      {section('budget', <>
        <Paycheck {...props} />
        <Settings {...props} only="budget" />
      </>)}
      {section('bills', <Settings {...props} only="bills" />)}
      {section('debt', <Debt {...props} />)}
      {section('savings', <>
        <Savings {...props} />
        <SinkingFunds state={state} updateState={updateState} showToast={showToast} />
      </>)}
      {section('networth', <NetWorthTracker state={state} updateState={updateState} showToast={showToast} />)}
      {section('bank', <BankBalanceCard state={state} updateState={updateState} />)}
      {section('checkin', <CheckIn {...props} />)}
    </div>
  );
}
